import { DataProvider, HttpError } from 'react-admin';
import { gql, ApolloError } from '@apollo/client';
import apolloClient from './apolloClient';

/**
 * EKAA Admin Data Provider
 * Maps React Admin operations to existing EKAA GraphQL API
 */

// Storage key for token (must match App.tsx)
const TOKEN_KEY = 'ekaa_admin_token';

// Helper to get Apollo client with fresh token
function getClient() {
  return apolloClient;
}

/**
 * Handle Apollo errors and convert to React Admin errors
 */
function handleApolloError(error: any): never {
  if (error instanceof ApolloError) {
    // Check for auth errors
    const authError = error.graphQLErrors?.find(
      (e: any) =>
        e.extensions?.code === 'UNAUTHENTICATED' ||
        e.extensions?.code === 'FORBIDDEN' ||
        e.message?.includes('Unauthorized')
    );

    if (authError) {
      // Clear session and throw HttpError with 401 status
      // React Admin's authProvider.checkError will handle the redirect
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('ekaa_admin_refresh_token');
      localStorage.removeItem('ekaa_admin_user');
      throw new HttpError('Session expirée. Veuillez vous reconnecter.', 401);
    }

    // Network errors
    if (error.networkError) {
      throw new HttpError('Erreur réseau. Vérifiez votre connexion.', 503);
    }

    // Other GraphQL errors
    const message = error.graphQLErrors?.[0]?.message || error.message || 'Erreur inconnue';
    throw new HttpError(message, 400);
  }

  // Unknown error type
  throw new HttpError(error.message || 'Erreur inattendue', 500);
}

/**
 * Get list of users (profiles)
 */
async function getUsersList(params: any) {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const filter = params.filter || {};

  const offset = (page - 1) * perPage;
  const limit = perPage;

  try {
    const { data } = await getClient().query({
      query: gql`
        query GetProfiles($search: String, $limit: Int, $offset: Int) {
          publicProfiles(search: $search, limit: $limit, offset: $offset) {
            id
            userId
            email
            username
            firstName
            lastName
            displayName
            avatarUrl
            jobTitle
            company
            city
            country
            sector
            profileViews
            verifiedBadge
            isPremium
            slug
            createdAt
          }
          profilesCount(search: $search)
        }
      `,
      variables: {
        search: filter.q || filter.search || undefined,
        limit,
        offset,
      },
      fetchPolicy: 'network-only',
    });

    return {
      data: data.publicProfiles || [],
      total: data.profilesCount || 0,
    };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * Get single user (profile) by slug
 */
async function getUserOne(params: any) {
  try {
    const { data } = await getClient().query({
      query: gql`
        query GetProfile($slug: String!) {
          profile(slug: $slug) {
            id
            userId
            email
            username
            firstName
            lastName
            displayName
            avatarUrl
            bio
            jobTitle
            company
            city
            country
            sector
            profileViews
            verifiedBadge
            isPremium
            slug
            createdAt
            updatedAt
          }
        }
      `,
      variables: { slug: params.id },
      fetchPolicy: 'network-only',
    });

    return { data: data.profile };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * Get list of cards
 */
async function getCardsList(params: any) {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const filter = params.filter || {};

  const offset = (page - 1) * perPage;
  const limit = perPage;

  // Extract search and filter values
  const search = filter.q || filter.search || undefined;
  const status = filter.status || undefined;
  const cardType = filter.cardType || undefined;

  try {
    const { data } = await getClient().query({
      query: gql`
        query GetAllCards($limit: Int, $offset: Int, $search: String, $status: String, $cardType: String) {
          allDigitalCards(limit: $limit, offset: $offset, search: $search, status: $status, cardType: $cardType) {
            id
            cardType
            displayName
            title
            company
            slug
            viewCount
            shareCount
            status
            theme
            createdAt
            location
            businessData
            eventData
          }
          digitalCardsCount(search: $search, status: $status, cardType: $cardType)
        }
      `,
      variables: { limit, offset, search, status, cardType },
      fetchPolicy: 'network-only',
    });

    return {
      data: data.allDigitalCards || [],
      total: data.digitalCardsCount || 0,
    };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * Get single card by ID
 */
async function getCardOne(params: any) {
  try {
    const { data } = await getClient().query({
      query: gql`
        query GetCard($id: String!) {
          cardById(id: $id) {
            id
            cardType
            displayName
            title
            company
            bio
            slug
            viewCount
            shareCount
            status
            theme
            assets
            location
            businessCategory
            createdAt
            updatedAt
          }
        }
      `,
      variables: { id: params.id },
      fetchPolicy: 'network-only',
    });

    return { data: data.cardById };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * Create a new card
 */
async function createCard(params: any) {
  try {
    const { data } = await getClient().mutate({
      mutation: gql`
        mutation CreateCard($input: DigitalCardUpdateInput!) {
          createCard(input: $input) {
            id
            cardType
            displayName
            title
            slug
            status
            createdAt
          }
        }
      `,
      variables: { input: params.data },
    });

    return { data: data.createCard };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * Update a card
 * Only send editable fields to avoid GraphQL validation errors
 */
async function updateCard(params: any) {
  // Extract only editable fields (exclude read-only and system fields)
  const {
    id,
    __typename,
    viewCount,
    shareCount,
    slug,
    publicUrl,
    isActive,
    createdAt,
    updatedAt,
    assets, // Exclude assets - can be very large base64
    ...editableData
  } = params.data;

  // Only include fields that are actually editable
  const input: any = {};
  if (editableData.displayName !== undefined) input.displayName = editableData.displayName;
  if (editableData.title !== undefined) input.title = editableData.title;
  if (editableData.company !== undefined) input.company = editableData.company;
  if (editableData.bio !== undefined) input.bio = editableData.bio;
  if (editableData.status !== undefined) input.status = editableData.status;
  if (editableData.cardType !== undefined) input.cardType = editableData.cardType;
  if (editableData.location !== undefined) input.location = editableData.location;
  if (editableData.businessCategory !== undefined) input.businessCategory = editableData.businessCategory;

  try {
    const { data } = await getClient().mutate({
      mutation: gql`
        mutation UpdateCard($cardId: String!, $input: DigitalCardUpdateInput!) {
          updateCard(cardId: $cardId, input: $input) {
            id
            cardType
            displayName
            title
            slug
            status
            updatedAt
          }
        }
      `,
      variables: {
        cardId: params.id,
        input,
      },
    });

    return { data: data.updateCard };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * Delete a card
 */
async function deleteCard(params: any) {
  try {
    await getClient().mutate({
      mutation: gql`
        mutation DeleteCard($cardId: String!) {
          deleteCard(cardId: $cardId)
        }
      `,
      variables: { cardId: params.id },
    });

    return { data: params.previousData || { id: params.id } };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * Archive/unarchive a card
 */
async function archiveCard(cardId: string) {
  try {
    const { data } = await getClient().mutate({
      mutation: gql`
        mutation ArchiveCard($cardId: String!) {
          archiveCard(cardId: $cardId) {
            id
            status
          }
        }
      `,
      variables: { cardId },
    });

    return { data: data.archiveCard };
  } catch (error) {
    handleApolloError(error);
  }
}

/**
 * EKAA Admin Data Provider
 */
export const dataProvider: DataProvider = {
  // ============== GET LIST ==============
  getList: async (resource, params) => {
    switch (resource) {
      case 'users':
        return getUsersList(params);
      case 'cards':
        return getCardsList(params);
      default:
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[DataProvider] Resource non gérée: ${resource}`);
        }
        return Promise.resolve({ data: [], total: 0 });
    }
  },

  // ============== GET ONE ==============
  getOne: async (resource, params) => {
    switch (resource) {
      case 'users':
        return getUserOne(params);
      case 'cards':
        return getCardOne(params);
      default:
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[DataProvider] Resource non gérée: ${resource}`);
        }
        return Promise.resolve({ data: { id: params.id } });
    }
  },

  // ============== GET MANY ==============
  getMany: async (resource, params) => {
    // Fetch one by one
    const results = await Promise.all(
      params.ids.map((id) => dataProvider.getOne(resource, { id: String(id) }))
    );
    return { data: results.map((r) => r.data) };
  },

  // ============== GET MANY REFERENCE ==============
  getManyReference: async (resource, params) => {
    // Add target filter
    const filter = {
      ...params.filter,
      [params.target]: params.id,
    };
    return dataProvider.getList(resource, { ...params, filter });
  },

  // ============== CREATE ==============
  create: async (resource, params) => {
    switch (resource) {
      case 'cards':
        return createCard(params);
      default:
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[DataProvider] Create non supporté pour la ressource: ${resource}`);
        }
        return Promise.resolve({ data: { id: 'unsupported', ...params.data } });
    }
  },

  // ============== UPDATE ==============
  update: async (resource, params) => {
    switch (resource) {
      case 'cards':
        return updateCard(params);
      default:
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[DataProvider] Update non supporté pour la ressource: ${resource}`);
        }
        return Promise.resolve({ data: { id: params.id, ...params.data } });
    }
  },

  // ============== UPDATE MANY ==============
  updateMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        dataProvider.update(resource, {
          id: String(id),
          data: params.data,
          previousData: {} as any,
        })
      )
    );
    return { data: params.ids.map(String) };
  },

  // ============== DELETE ==============
  delete: async (resource, params) => {
    switch (resource) {
      case 'cards':
        return deleteCard(params);
      default:
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[DataProvider] Delete non supporté pour la ressource: ${resource}`);
        }
        return Promise.resolve({ data: params.previousData || { id: params.id } });
    }
  },

  // ============== DELETE MANY ==============
  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        dataProvider.delete(resource, {
          id: String(id),
          previousData: { id: String(id) } as any,
        })
      )
    );
    return { data: params.ids.map(String) };
  },
};

// Export archive function for custom use
export { archiveCard };

export default dataProvider;
